import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import Select from "react-select";
import { Link } from "react-router-dom";
import CategoryServices from "../../../services/OrderingServices/CategoryService";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import toast from "react-hot-toast";
import { exportToExcel } from "../../../utilis/exportToExcel";

const SummaryCard = ({ title, value, color, icon }) => (
  <Card className="shadow-sm card-animate border-0">
    <CardBody className="d-flex align-items-center justify-content-between">
      <div className="w-100">
        <h6 className="fw-semibold text-uppercase" style={{ color }}>
          {title}
        </h6>
        <div className="d-flex justify-content-between w-100">
          <h4 className="fs-2 fw-bold" style={{ color }}>
            {value}
          </h4>
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: color + "20",
              color: color,
              width: "36px",
              height: "36px",
            }}
          >
            <i className={`${icon} fs-5`} />
          </div>
        </div>
      </div>
    </CardBody>
  </Card>
);

const ItemWiseReport = ({ categoryData }) => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filterText, setFilterText] = useState("");

  // Export modal states
  const [exportModal, setExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  // Format helper
  const formatDate = (date) => date?.toLocaleDateString("en-CA");

  // Fetch item-wise data
  const fetchData = async (
    from = fromDate,
    to = toDate,
    category = null,
    product = filterText
  ) => {
    if (!from || !to) return;

    setLoading(true);
    try {
      const response = await SalesReportService.itemWise({
        itemName: product,
        categoryId: category?.value || null,
        fromDate: formatDate(from),
        toDate: formatDate(to),
      });
      setAllData(response || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Product Name",
      selector: (row) => row.itemName,
      sortable: true,
      cell: (row) => (
        <Link to={`/transaction/report/${row?.itemId}`}>{row.itemName}</Link>
      ),
    },
    {
      name: "Category Name",
      selector: (row) => row.categoryName,
      sortable: true,
    },

    {
      name: "Complimentary Qty",
      selector: (row) => row.complimentryQuantity,
      sortable: true,
      right: true,
      width: "200px",
    },
    {
      name: "Total Quantity",
      selector: (row) => row.totalQuantity,
      sortable: true,
      right: true,
      width: "160px",
    },
    {
      name: "Total Sales",
      selector: (row) => row.totalSales,
      sortable: true,
      right: true,
      width: "150px",
      cell: (row) => <NumberWithCommas number={row.totalSales} />,
    },
  ];

  // Initialize dates & categories on mount, auto-fetch last 1 month
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);

    fetchData(lastMonth, today, null); // initial fetch for last 1 month
  }, []);

  // Auto-fetch only when category changes
  useEffect(() => {
    if (fromDate && toDate) {
      fetchData(fromDate, toDate, filterText);
    }
  }, [filterText]); // only listen to category

  // Default: last 1 month on mount + auto-fetch
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);
    setDateRange([lastMonth, today]);

    fetchData(lastMonth, today, null);
  }, []);
  // totals
  const totalQuantity = allData.reduce(
    (sum, item) => sum + item.totalQuantity,
    0
  );
  const totalSales = allData.reduce((sum, item) => sum + item.totalSales, 0);
  const totalComplimentary = allData.reduce(
    (sum, item) => sum + (item.complimentryQuantity || 0),
    0
  );

  // category dropdown options
  const categoryOption = categoryData?.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const getDateRangeDisplay = () => {
    if (!fromDate || !toDate) return "Select Date Range";
    const start = new Date(fromDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const end = new Date(toDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${start} - ${end}`;
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const toggleExportModal = () => {
    setExportModal(!exportModal);
    if (!exportModal) {
      // Reset date range when opening modal
      setExportDateRange([]);
    }
  };

  // Handle export with date range
  const handleExportWithDateRange = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select a valid date range");
      return;
    }

    try {
      setExportLoading(true);

      const startDate = formatLocalDate(fromDate);
      const endDate = formatLocalDate(toDate);

      // Fetch all data with selected date range (no pagination for export)
      const response = await SalesReportService.itemWise({
        pageNumber: 1,
        perPage: 10000, // Large number to get all records
        fromDate: startDate,
        toDate: endDate,
      });

      const exportData = response || [];

      if (exportData.length === 0) {
        toast.error("No data found for the selected date range");
        setExportLoading(false);
        return;
      }

      // Column mapping for Excel export
      const columnMapping = {
        "S.N": (row, index) => index + 1,
        "Product Name.": "itemName",
        "Category Name": "categoryName",
        "Complimentary Qty": "complimentryQuantity",
        "Total Quantity": "totalQuantity",
        "Total Sales": "totalSales",
      };

      exportToExcel(
        exportData,
        columnMapping,
        `Product_Wise_Report_${startDate}_to_${endDate}.xlsx`
      );

      toast.success("Export successful!");
      setExportLoading(false);
      toggleExportModal();
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
      setExportLoading(false);
    }
  };
  return (
    <>
      {/* Main Content */}
      <div className="p-3 p-lg-4 mt-1">
        {/* <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1 fw-bold text-dark">Product Wise Report</h5>
            <p className="text-muted small mb-0">{getDateRangeDisplay()}</p>
          </div>
          <Button
            color="success"
            onClick={handleExportWithDateRange}
            className="d-flex align-items-center gap-2"
            style={{ borderRadius: "8px" }}
          >
            <i className="bx bx-export"></i> Export
          </Button>
        </div>

        <hr style={{ borderColor: "#dee2e6", marginTop: "0rem" }} /> */}

        {/* Filters */}
        <Row className="mb-3 g-2 align-items-center">
          {/* Title & Date Display */}
          <Col md={4}>
            <div>
              <h5 className="mb-1 fw-bold text-dark">Product Wise Report</h5>
              <p className="text-muted small mb-0">{getDateRangeDisplay()}</p>
            </div>
          </Col>

          {/* Filters & Buttons */}
          <Col md={8}>
            <Row className="g-2 align-items-end">
              {/* Product Name Filter */}
              <Col md={4}>
                <InputGroup>
                  <Input
                    type="text"
                    placeholder="Filter by Product Name"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  <InputGroupText>
                    <i className="bx bx-search"></i>
                  </InputGroupText>
                </InputGroup>
              </Col>

              {/* Date Range Picker */}
              <Col md={4}>
                <InputGroup>
                  <Flatpickr
                    options={{ mode: "range", dateFormat: "Y-m-d" }}
                    value={dateRange}
                    onChange={(dates) => {
                      if (dates.length === 2) {
                        setDateRange(dates);
                        setFromDate(dates[0]);
                        setToDate(dates[1]);
                      }
                    }}
                    placeholder="Select Date Range"
                    className="form-control"
                  />
                  <InputGroupText style={{ cursor: "pointer" }}>
                    <i className="bx bx-calendar"></i>
                  </InputGroupText>
                </InputGroup>
              </Col>

              {/* Generate Button */}
              <Col md={2}>
                <Button
                  color="primary"
                  className="w-100"
                  onClick={() => fetchData()}
                >
                  <i className="bx bx-search-alt me-1"></i>
                  Generate
                </Button>
              </Col>

              {/* Export Button */}
              <Col md={2}>
                <Button
                  color="success"
                  onClick={handleExportWithDateRange}
                  className="w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{ borderRadius: "8px" }}
                >
                  <i className="bx bx-export"></i>
                  Export
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Table */}
        <CustomDataTable
          responsive
          striped
          fixedHeader
          highlightOnHover
          pointerOnHover
          persistTableHead
          progressPending={loading}
          columns={columns}
          customStyles={{
            headCells: {
              style: {
                fontSize: "0.92rem",
                fontWeight: 610,
              },
            },
            rows: {
              style: {
                fontSize: "0.9rem",
              },
            },
          }}
          progressComponent={
            <div className="my-3 text-center">
              <Triangle visible height="80" width="80" color="#5B71B9" />
              <h5 className="mt-1">Loading...</h5>
            </div>
          }
          data={allData}
          noDataComponent={
            <div className="text-center my-5 w-100">
              <i className="ri-emotion-unhappy-line display-4 text-secondary" />
              <h5 className="fw-semibold text-secondary">No Data Found</h5>
              <p className="text-muted mb-0">
                No item data available for the selected filters.
              </p>
            </div>
          }
        />

        {/* Summary Footer */}
        {allData.length > 0 && (
          <div
            className="p-3 mt-3"
            style={{
              background: "#f8f9fa",
              borderRadius: "0.375rem",
              border: "1px solid #dee2e6",
            }}
          >
            <Row className="fw-bold">
              <Col md={4}>
                <h6 className="mb-0 text-dark">Total Summary</h6>
              </Col>
              <Col md={8}>
                <Row>
                  <Col md={4} className="text-end">
                    <span className="text-muted me-2">Complimentary:</span>
                    <span className="text-dark">{totalComplimentary}</span>
                  </Col>
                  <Col md={4} className="text-end">
                    <span className="text-muted me-2">Quantity:</span>
                    <span className="text-dark">{totalQuantity}</span>
                  </Col>
                  <Col md={4} className="text-end">
                    <span className="text-muted me-2">Sales:</span>
                    <span className="text-dark">
                      <NumberWithCommas number={totalSales} />
                    </span>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </>
  );
};

export default ItemWiseReport;
